import { Injectable, inject } from '@angular/core';
import { Observable, catchError, defer, finalize, tap, throwError } from 'rxjs';
import { ResponseDto } from '@core/utils/api.dto';
import { AuthStore } from '@core/auth/auth.store';
import { ProjectContextStore } from './project-context.store';
import { ProjectContextApi } from './project-context.api';

const SWITCHING_MESSAGE = 'components.loader.changingProject';
const REJECTED_MESSAGE = 'Unknown error occurred while setting selected project.';
const UNREACHABLE_MESSAGE = 'Could not reach the server to change the project.';

@Injectable({ providedIn: 'root' })
export class ProjectContextService {
  private readonly authStore = inject(AuthStore);
  private readonly projectContextStore = inject(ProjectContextStore);
  private readonly projectContextApi = inject(ProjectContextApi);

  /**
   * Switches the active project, with the context store as the single place the
   * outcome is reported. Failures still reach the caller, so a request that
   * never landed is never mistaken for a completed switch.
   */
  setSelectedProject(projectId: number): Observable<ResponseDto<boolean>> {
    return defer(() => {
      this.projectContextStore.beginLoading(SWITCHING_MESSAGE);
      return this.projectContextApi.setSelectedProject(projectId);
    }).pipe(
      tap((response) => {
        if (response.success) {
          this.projectContextStore.setActiveProjectId(projectId);
          this.authStore.setLastProjectSelected(projectId);
        } else {
          this.projectContextStore.setError(response.error ?? REJECTED_MESSAGE);
          console.error('Error setting selected project in ProjectContextService:', response.error);
        }
      }),
      catchError((error: unknown) => {
        this.projectContextStore.setError(UNREACHABLE_MESSAGE);
        console.error('Error setting selected project in ProjectContextService:', error);

        return throwError(() => error);
      }),
      finalize(() => this.projectContextStore.endLoading()),
    );
  }
}
